import { GameIndicator, GameModel, GameTimeChallenge, HasGameTimeChallenge } from '@/models/GameModel';
import {
  GameHitTheTargetAdvancedData,
  GameHitTheTargetData,
  GameHitTheTargetGeneralData,
  GameHitTheTargetImagesData,
  GameHitTheTargetImagesSymbolData
} from '@/components/games/hitthetarget/Data';
import useDevice from '@/hooks/useDevice';
import { GameIndicatorIcon } from '@/components/indicators/Model';

export interface GameHitTheTargetGeneralGameSettingsState {
  height: string;
  symbolWidth: number;
  symbolSpeed: number;
  overwriteMobile?: boolean;
}

export interface GameHitTheTargetGeneralState {
  pointsToWin: number;
  positiveColor: string;
  negativeColor: string;
  gameSettings: GameHitTheTargetGeneralGameSettingsState;
  enableBestTime: boolean;
  enableTimeRanking: boolean;
}

export interface GameHitTheTargetImagesSymbolState {
  id: number;
  image: string;
  points: number;
  label: string;
  direction: 'left_right' | 'right_left' | 'top_bottom' | 'bottom_top';
  degree: number;
}

export interface GameHitTheTargetImagesState {
  cursorImage: string;
  cursorImageOffsetX: number;
  cursorImageOffsetY: number;
  symbol: GameHitTheTargetImagesSymbolState[];
}

export interface GameHitTheTargetAdvancedSoundState {
  enabled: boolean;
  plusSound?: string;
  minusSound?: string;
}

export interface GameHitTheTargetAdvancedState {
  sound: GameHitTheTargetAdvancedSoundState;
}

export interface GameHitTheTargetState {
  general: GameHitTheTargetGeneralState;
  images: GameHitTheTargetImagesState;
  advanced: GameHitTheTargetAdvancedState;
}

export class GameHitTheTargetModel
  extends GameModel<GameHitTheTargetData, GameHitTheTargetState>
  implements HasGameTimeChallenge
{
  parseGame(data: GameHitTheTargetData): void {
    const state = this.state;
    state.general = GameHitTheTargetModel.constructGeneralState(data.general);
    state.images = GameHitTheTargetModel.constructImagesState(data.images);

    state.advanced = state.advanced ?? {};
    state.advanced.sound = GameHitTheTargetModel.constructAdvancedSoundState(data?.advanced);
  }

  public static constructAdvancedSoundState(
    data: GameHitTheTargetAdvancedData | undefined
  ): GameHitTheTargetAdvancedSoundState {
    return {
      enabled: !!data?.minus_sound || !!data?.plus_sound,
      ...(data?.minus_sound && {
        minusSound: data.minus_sound
      }),
      ...(data?.plus_sound && {
        plusSound: data.plus_sound
      })
    };
  }

  public static constructImagesSymbolState(data: GameHitTheTargetImagesSymbolData): GameHitTheTargetImagesSymbolState {
    return {
      id: Number(data.id),
      image: data.image,
      points: Number(data.points),
      label: data.label,
      direction: data.direction,
      degree: Number(data.degree)
    };
  }

  public static constructImagesState(data: GameHitTheTargetImagesData): GameHitTheTargetImagesState {
    return {
      cursorImage: data.cursor_image,
      cursorImageOffsetX: Number(data.cursor_image_offset_x),
      cursorImageOffsetY: Number(data.cursor_image_offset_y),
      symbol: data.symbol.map((symbol) => {
        return GameHitTheTargetModel.constructImagesSymbolState(symbol);
      })
    };
  }

  public static getMobileGeneralGameSettingsDeviceData(
    data: GameHitTheTargetGeneralData
  ): GameHitTheTargetGeneralGameSettingsState {
    const { isMobile } = useDevice();

    if (isMobile && data.game_settings.desktop.overwrite_mobile !== '1') {
      return {
        ...{ symbolWidth: parseInt(data.game_settings?.mobile?.symbol_width) },
        ...{ height: data.game_settings?.mobile?.height },
        ...{ symbolSpeed: Number(data.game_settings?.mobile?.symbol_speed) }
      };
    }

    return {
      ...{ symbolWidth: parseInt(data.game_settings.desktop.symbol_width) },
      ...{ height: data.game_settings.desktop.height },
      ...{ symbolSpeed: Number(data.game_settings.desktop.symbol_speed) }
    };
  }

  private static constructGeneralState(data: GameHitTheTargetGeneralData): GameHitTheTargetGeneralState {
    return {
      pointsToWin: Number(data.points_towin),
      positiveColor: data.positive_color,
      negativeColor: data.negative_color,
      gameSettings: GameHitTheTargetModel.getMobileGeneralGameSettingsDeviceData(data),
      ...(data?.time &&
        data.time === 1 && {
          timeEnabled: data.time === 1,
          timeLimit: Number(data.time_limit)
        }),
      enableBestTime: data.enable_best_time === '1',
      enableTimeRanking: data.enable_time_ranking === '1'
    };
  }

  public getIndicators(): GameIndicator[] {
    return [
      {
        indicatorKey: 'time',
        metricKey: {
          time_left: 'timeleft',
          time_used: 'timeused'
        },
        icon: GameIndicatorIcon.TIME,
        value: {
          time_left: this.state.timeChallenge?.limit || 60,
          time_used: 0
        }
      },
      {
        indicatorKey: 'score',
        metricKey: {
          score_used: 'points'
        },
        icon: GameIndicatorIcon.SCORE,
        value: {
          score_used: 0
        }
      }
    ];
  }

  parseTimeChallenge(): GameTimeChallenge | undefined {
    const data = this.getData();
    return {
      enabled: data.general.time === 1,
      ...(data.general.time_limit && { limit: Number(data.general.time_limit) })
    };
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen';
  }
}
